import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState, useEffect } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || '');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const catArticleCounts: Record<string, number> = {};
  articles.forEach((a) => {
    catArticleCounts[a.category] = (catArticleCounts[a.category] || 0) + 1;
  });

  return (
    <Layout>
      <SEOMeta
        title={`${currentCategory.name} - 地址发布页 | ${currentCategory.description}`}
        description={`地址发布页${currentCategory.name}栏目，${currentCategory.description}。提供最新官方地址入口导航与安全访问指南。`}
        keywords={`${currentCategory.name},地址发布页,官方地址入口,${currentCategory.name}指南`}
        canonicalUrl={`https://gtucxr.cn/category/${activeSlug}`}
        ogType="website"
      />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="border-b border-background-200 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1.5 text-xs opacity-70">
                    ({catArticleCounts[cat.slug] || 0})
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <article
                  key={article.slug}
                  className="bg-background-50 rounded-xl border border-background-200 p-5 hover:border-primary-200 transition-all duration-300 group cursor-pointer"
                >
                  <Link to={`/article/${article.slug}`} className="block">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-primary-100 text-primary-700 text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                        {article.categoryName}
                      </span>
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-900 text-base mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          )}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://gtucxr.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory.name,
                item: `https://gtucxr.cn/category/${activeSlug}`,
              },
            ],
          }),
        }}
      />
    </Layout>
  );
}